import type { UnifiedExecutiveContext } from '../types/unifiedExecutiveContext'
import type {
  AssetInventoryExecutiveAsset,
  InformationSystemExecutiveSummary,
  RiskComplianceStandardRow,
  DataMapFlowOperationalSummary,
} from '../services/enterpriseOperationalUxApi'

export function mapInformationSystemToUnifiedContext(
  summary: InformationSystemExecutiveSummary,
  systemName: string,
): UnifiedExecutiveContext {
  return {
    scope: 'informationSystem',
    entityId: summary.informationSystemId,
    title: systemName,
    pressure: {
      band: summary.pressure,
      value: summary.operationalPressure,
      criticality: summary.executiveCriticality,
    },
    stability: {
      level: summary.certificationStability,
      trajectory: summary.readinessTrajectory,
    },
    trajectory: summary.readinessTrajectory,
    runtimeEvents: [
      ...(summary.latestRuntimeEvent ? [summary.latestRuntimeEvent] : []),
      ...(summary.recentRuntimeEvents || []).filter(
        (e) => e.id !== summary.latestRuntimeEvent?.id,
      ),
    ].map((e) => ({
      id: e.id,
      at: e.at,
      eventType: e.eventType,
      severity: e.severity,
      summary: e.summary,
      relatedStandardCode: 'relatedStandardCode' in e ? e.relatedStandardCode : null,
    })),
    blastRadius: {
      impacts: [],
      hits: (summary.sharedControlBlastRadius || []).map((h) => ({
        controlCode: h.controlCode,
        summary: h.summary,
        impactedStandards: h.impactedStandards || [],
        standardCount: h.standardCount,
        systemCount: h.systemCount,
        severity: h.severity,
      })),
    },
    degradedControls: summary.degradedControlCount,
    staleEvidence: summary.staleEvidenceCount,
    blockers: summary.blockerCount,
    workflows: summary.workflowStages || [],
    impactedStandards: summary.impactedStandards || [],
    impactedSystems: [],
    recommendations: summary.recommendedNextActions || [],
    explanations: summary.explanations || [],
    storytelling: summary.explanations?.join(' · ') || null,
    hasEnoughHistory: summary.hasEnoughHistory,
  }
}

export function mapAssetToUnifiedContext(asset: AssetInventoryExecutiveAsset): UnifiedExecutiveContext {
  return {
    scope: 'asset',
    entityId: String(asset.assetId),
    title: asset.name,
    pressure: {
      band: asset.pressure.band,
      value: asset.pressure.operationalPressure,
      criticality: asset.exposure.exposureLevel,
    },
    stability: { level: null, trajectory: null },
    trajectory: null,
    runtimeEvents: [
      ...(asset.runtime.latestEvent ? [asset.runtime.latestEvent] : []),
      ...(asset.runtime.recentEvents || []),
    ].map((e) => ({
      id: e.id,
      at: e.at,
      eventType: e.eventType,
      severity: 'severity' in e ? String((e as { severity?: string }).severity || 'INFO') : 'INFO',
      summary: e.summary,
    })),
    blastRadius: {
      impacts: [],
      hits: (asset.blastRadius.hits || []).map((h) => ({
        controlCode: h.controlCode,
        summary: h.summary,
        impactedStandards: h.impactedStandards || [],
        standardCount: h.standardCount,
        systemCount: h.systemCount,
        severity: h.severity,
      })),
    },
    degradedControls: asset.pressure.degradedControls,
    staleEvidence: asset.pressure.staleEvidenceCount,
    blockers: asset.pressure.blockers,
    workflows: asset.workflow.stages || [],
    impactedStandards: asset.complianceApplicability.standardsAffected || [],
    impactedSystems: [],
    recommendations: asset.recommendedNextActions || [],
    explanations: asset.storytelling ? [asset.storytelling] : [],
    storytelling: asset.storytelling,
    hasEnoughHistory: true,
  }
}

export function mapStandardRowToUnifiedContext(row: RiskComplianceStandardRow): UnifiedExecutiveContext {
  return {
    scope: 'standard',
    entityId: row.standardCode,
    title: row.standardName || row.standardCode,
    pressure: { band: row.pressure, value: row.operationalPressure, criticality: null },
    stability: { level: row.certificationRiskLevel, trajectory: row.readinessTrajectory },
    trajectory: row.readinessTrajectory,
    runtimeEvents: [
      ...(row.latestRuntimeEvent ? [row.latestRuntimeEvent] : []),
      ...(row.recentRuntimeEvents || []),
    ].map((e) => ({
      id: e.id,
      at: e.at,
      eventType: e.eventType,
      severity: 'severity' in e ? String((e as { severity?: string }).severity || 'INFO') : 'INFO',
      summary: e.summary,
      relatedStandardCode: row.standardCode,
    })),
    blastRadius: { impacts: [], hits: [] },
    degradedControls: row.degradedControlCount,
    staleEvidence: row.staleEvidenceCount,
    blockers: row.blockerCount,
    workflows: row.workflowStage
      ? [{ standardCode: row.standardCode, workflowStage: row.workflowStage }]
      : [],
    impactedStandards: [row.standardCode],
    impactedSystems: [],
    recommendations: [],
    explanations: row.storytelling ? [row.storytelling] : [],
    storytelling: row.storytelling,
    hasEnoughHistory: row.hasEnoughHistory,
  }
}

export function mapFlowToUnifiedContext(flow: DataMapFlowOperationalSummary): UnifiedExecutiveContext {
  return {
    scope: 'flow',
    entityId: flow.id,
    title: `${flow.sourceSystemName} → ${flow.targetSystemName}`,
    pressure: {
      band: flow.operationalState.operationalRiskLevel,
      value: 0,
      criticality: null,
    },
    stability: { level: null, trajectory: null },
    trajectory: null,
    runtimeEvents: (flow.runtime.recentEvents || []).map((e) => ({
      id: e.id,
      at: e.at,
      eventType: e.eventType,
      severity: e.severity || 'INFO',
      summary: e.summary,
    })),
    blastRadius: {
      impacts: [],
      hits: (flow.blastRadius.blastHits || []).map((h) => ({
        controlCode: h.controlCode,
        summary: h.summary,
        impactedStandards: h.impactedStandards || [],
        standardCount: h.standardCount || 0,
        systemCount: h.systemCount || 0,
        severity: h.severity || 'MODERATE',
      })),
    },
    degradedControls: 0,
    staleEvidence: 0,
    blockers: 0,
    workflows: [],
    impactedStandards: flow.blastRadius.impactedStandards || [],
    impactedSystems: [flow.sourceSystemId, flow.targetSystemId],
    recommendations: [],
    explanations: flow.storytelling ? [flow.storytelling] : [],
    storytelling: flow.storytelling,
    hasEnoughHistory: true,
  }
}
